import { Stack } from "@certn/cascadia";
import {
  LinkButton,
  ModalTrigger,
  TextField,
  Toggle,
} from "@certn/cascadia-react";
import { CascadiaJsonFormContext } from "@certn/forms/src/CascadiaJsonFormContext";
import { SummaryItem } from "@certn/forms/src/components/SummaryItem";
import {
  CascadiaLayoutProps,
  withCascadiaLayoutProps,
} from "@certn/forms/src/wrappers/withCascadiaLayoutProps";
import { css } from "@emotion/css";
import { useEffect, useState, useContext } from "react";
import { Trans, useTranslation } from "react-i18next";

import { LearnMoreModal } from "./LearnMoreModal";
import { useHandleChange } from "../../utils";

function DbsProfileNumberControl(props: CascadiaLayoutProps) {
  const { t } = useTranslation();
  const { data = {} } = props;
  const { isSummary } = useContext(CascadiaJsonFormContext);
  const handleChange = useHandleChange();

  const [showError, setShowError] = useState(false);

  useEffect(() => {
    if (!data.dbs_profile_number) {
      handleChange("dbs_profile_number", {
        has_dbs_profile_number: false,
        number: "",
      });
    }
  }, [data, handleChange]);

  const hasDbsProfileNumber = Boolean(
    data?.dbs_profile_number?.has_dbs_profile_number
  );
  const profileNumber = data?.dbs_profile_number?.number || "";

  if (isSummary) {
    return (
      <Stack gap="space-0">
        <SummaryItem label={t("I have a DBS number")}>
          {hasDbsProfileNumber ? t("Yes") : t("No")}
        </SummaryItem>

        {hasDbsProfileNumber && (
          <SummaryItem label={t("DBS Profile Number")}>
            {profileNumber}
          </SummaryItem>
        )}
      </Stack>
    );
  }

  return (
    <Stack orientation="horizontal" align="start">
      <Stack className={css(styles.flexOne)}>
        <Toggle
          name="has_dbs_profile_number"
          testID="has_dbs_profile_number"
          aria-label="has_dbs_profile_number"
          onChange={(value) => {
            handleChange("dbs_profile_number.has_dbs_profile_number", value);
            if (!value) {
              setShowError(false);
              handleChange("dbs_profile_number.number", "");
            }
          }}
          description={t(
            "If you already have a DBS online account you will have a DBS Profile Number. If so, select the toggle and enter your number."
          )}
          defaultSelected={hasDbsProfileNumber}
        >
          <Trans>I have a DBS number</Trans>
        </Toggle>

        {hasDbsProfileNumber && (
          <TextField
            label={t("Enter your DBS Profile Number")}
            testID="dbs_profile_number"
            value={profileNumber}
            onChange={(value) => {
              handleChange("dbs_profile_number.number", value);
              setShowError(!value);
            }}
            onBlur={() => {
              setShowError(!profileNumber);
            }}
            isRequired
            isInvalid={showError}
            errors={
              showError
                ? [t("DBS Profile Number is a required field")]
                : undefined
            }
          />
        )}
      </Stack>

      <ModalTrigger
        trigger={
          <LinkButton
            linkStyle="monochrome"
            textStyle="global/body-secondary-link"
            textColor="text-weak"
            testID="learn-more"
          >
            <Trans>Learn more</Trans>
          </LinkButton>
        }
      >
        <LearnMoreModal />
      </ModalTrigger>
    </Stack>
  );
}

const styles = {
  flexOne: css({
    flex: 1,
  }),
};

DbsProfileNumberControl.displayName = "DbsProfileNumber";

export const DbsProfileNumberControlRenderer = withCascadiaLayoutProps(
  DbsProfileNumberControl
);
